// client/src/components/Achievements/Trophy.jsx
import React, { useState } from "react";

const Trophy = React.memo(function Trophy({
  icon,
  label,
  description = "",
  color,
  delay,
  status,
  progress = 0,
  target = 0,
}) {
  const [isInfoPinned, setIsInfoPinned] = useState(false);

  const uiStatus =
    status === "progress" &&
    Math.round((Number(progress || 0) / Math.max(1, Number(target || 1))) * 100) === 0
      ? "locked"
      : status;

  const unlockGuide = description
    ? String(description).trim()
    : Number(target || 0) > 0
    ? `Reach ${Number(target || 0)} progress to unlock this trophy.`
    : "Complete the required activity to unlock this trophy.";

  if (uiStatus === "locked") {
    return (
      <div
        className="relative z-0 flex flex-col items-center group badge-locked cursor-help p-3 rounded-xl border border-gray-100 hover:z-30 focus-within:z-30"
        role="button"
        tabIndex={0}
        onClick={() => setIsInfoPinned((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsInfoPinned((v) => !v);
          }
        }}
      >
        <div className="w-14 h-14 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-400 text-xl mb-2">
          <i className={`fa-solid ${icon}`}></i>
        </div>

        <span
          className="text-[10px] font-bold text-gray-400 text-center leading-tight"
          dangerouslySetInnerHTML={{ __html: String(label).replace(" ", "<br>") }}
        ></span>

        <span className="text-[10px] text-gray-400 mt-1">Locked</span>

        <div
          className={`absolute left-1/2 bottom-full z-40 mb-2 w-56 -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-2.5 text-[10px] text-gray-600 shadow-lg transition ${
            isInfoPinned
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
          }`}
        >
          <p className="font-bold text-gray-700 mb-1">How to get this trophy</p>
          <p>{unlockGuide}</p>
        </div>
      </div>
    );
  }

  const gradients = {
    yellow:
      "from-yellow-100 to-yellow-50 border-yellow-200 text-yellow-600 shadow-[0_4px_20px_0px_rgba(245,158,11,0.15)]",
    blue:
      "from-blue-100 to-blue-50 border-blue-200 text-blue-600 shadow-[0_4px_20px_0px_rgba(59,130,246,0.15)]",
    indigo:
      "from-indigo-100 to-indigo-50 border-indigo-200 text-indigo-600 shadow-[0_4px_20px_0px_rgba(59,130,246,0.15)]",
    orange:
      "from-orange-100 to-orange-50 border-orange-200 text-orange-600 shadow-[0_4px_20px_0px_rgba(245,158,11,0.15)]",
    teal:
      "from-teal-100 to-teal-50 border-teal-200 text-teal-600 shadow-[0_4px_20px_0px_rgba(59,130,246,0.15)]",
  };

  const completed = uiStatus === "completed";

  return (
    <div
      className={`relative z-0 flex flex-col items-center group p-3 rounded-xl border animate-bounce-in hover:z-30 focus-within:z-30 ${
        completed ? "border-green-200 bg-green-50/40" : "border-gray-100 bg-white"
      }`}
      style={{ animationDelay: delay }}
      role="button"
      tabIndex={0}
      onClick={() => setIsInfoPinned((v) => !v)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsInfoPinned((v) => !v);
        }
      }}
    >
      <div
        className={`w-14 h-14 rounded-full bg-gradient-to-br border flex items-center justify-center text-xl group-hover:-translate-y-1 transition-transform mb-2 ${
          gradients[color] || gradients.yellow
        }`}
      >
        <i className={`fa-solid ${icon}`}></i>
      </div>

      <span
        className="text-[10px] font-bold text-gray-600 text-center leading-tight"
        dangerouslySetInnerHTML={{ __html: String(label).replace(" ", "<br>") }}
      ></span>

      {completed && <span className="text-[10px] text-green-600 font-bold mt-1">Unlocked</span>}

      {uiStatus === "progress" && (
        <span className="text-[10px] text-gray-500 mt-1">
          {Number(progress || 0)}/{Number(target || 0)}
        </span>
      )}

      <div
        className={`absolute left-1/2 bottom-full z-40 mb-2 w-56 -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-2.5 text-[10px] text-gray-600 shadow-lg transition ${
          isInfoPinned
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
        }`}
      >
        <p className="font-bold text-gray-700 mb-1">How to get this trophy</p>
        <p>{unlockGuide}</p>
      </div>
    </div>
  );
});

export default Trophy;
